import React, { useEffect, useState, useRef, Fragment, lazy, memo, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Package, Star, X, Minus, MessageSquare, AlertCircle, Brain, DollarSign, BookX, Video, HardHat, Flame, Building2, CircuitBoard, Cpu, Settings, Target, TrendingUp, Award, GitBranch, Lightbulb, Headphones, ChevronLeft, ChevronRight, Eye, Ticket } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-32">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-33">
        <span className="font-medium text-gray-900 pr-4" data-id="element-34">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-35" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-36" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-37">
          <p className="text-gray-700 leading-relaxed" data-id="element-38">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-39">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-40">
        <div className="aspect-[3/4] relative" data-id="element-41">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" loading="lazy" decoding="async" width={640} height={853} data-id="element-42" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-43">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-44">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-45">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-46" />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [urgencySpots, setUrgencySpots] = useState(15);
  const [upsellSpots, setUpsellSpots] = useState(() => {
    const saved = localStorage.getItem('upsellSpots');
    return saved ? Math.max(2, parseInt(saved, 10)) : 8;
  });
  const [mainBuyers, setMainBuyers] = useState(() => {
    const saved = localStorage.getItem('mainBuyers');
    return saved ? parseInt(saved, 10) : 3251;
  });
  const [mainSpots, setMainSpots] = useState(15);
  const [comboBuyers, setComboBuyers] = useState(() => {
    const saved = localStorage.getItem('comboBuyers');
    return saved ? parseInt(saved, 10) : 5162;
  });
  const [comboSpots, setComboSpots] = useState(7);
  // Persist buyers and upsellSpots to localStorage
  useEffect(() => {
    localStorage.setItem('mainBuyers', mainBuyers.toString());
  }, [mainBuyers]);
  useEffect(() => {
    localStorage.setItem('comboBuyers', comboBuyers.toString());
  }, [comboBuyers]);
  useEffect(() => {
    localStorage.setItem('upsellSpots', upsellSpots.toString());
  }, [upsellSpots]);
  useEffect(() => {
    // Viewers fluctuation (40-63 range)
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(63, prev + change));
      });
    }, 3000 + Math.random() * 4000);
    // Profesionales (social proof): increase 1-3 every 10s
    const mainBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setMainBuyers(prev => prev + increment);
    }, 10000);
    const comboBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setComboBuyers(prev => prev + increment);
    }, 10000);
    // Card 1 cupos (scarcity): decrease 1 every 15-40s, min 2
    const urgencySpotsInterval = setInterval(() => {
      setUrgencySpots(prev => Math.max(2, prev - 1));
    }, 15000 + Math.random() * 25000);
    // Upsell cupos (scarcity): decrease 1 every 120-180s, min 2
    const upsellSpotsInterval = setInterval(() => {
      setUpsellSpots(prev => Math.max(2, prev - 1));
    }, 120000 + Math.random() * 60000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(mainBuyersInterval);
      clearInterval(comboBuyersInterval);
      clearInterval(urgencySpotsInterval);
      clearInterval(upsellSpotsInterval);
    };
  }, []);
  return {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  };
}
// Current Date Hook
function useCurrentDate() {
  const [dateString, setDateString] = useState('');
  const [shortDate, setShortDate] = useState('');
  const [weekdayDate, setWeekdayDate] = useState('');
  useEffect(() => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    setDateString(now.toLocaleDateString('es-AR', options));
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    setShortDate(`${dd}/${mm}/${yyyy}`);
    // Weekday + day + month (e.g. "Lunes 16 de Marzo")
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    setWeekdayDate(`${weekdays[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`);
  }, []);
  return {
    dateString,
    shortDate,
    weekdayDate
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-47">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-48">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-49">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-50">
          <p data-id="element-51">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-52">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-53">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-54" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-55">
          <X className="w-5 h-5" data-id="element-56" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-57">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-58">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-59" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-60">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-61" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-62" />
        {error && <p className="text-red-500 text-sm" data-id="element-63">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-64">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Offer Selector Component
function OfferSelector({
  selected,
  onChange
}: {
  selected: 'solo' | 'combo';
  onChange: (val: 'solo' | 'combo') => void;
}) {
  return <div className="flex flex-col items-center mb-12 w-full max-w-4xl mx-auto px-2" data-id="element-65">
      <div className="flex flex-col md:flex-row w-full gap-4" data-id="element-66">
        {/* Solo Maestro Button */}
        <button onClick={() => onChange('solo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight ${selected === 'solo' ? 'bg-[#0a1a3a] border-blue-500 text-white animate-neon-blue z-10' : 'bg-[#0f0f0f] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-67">
          SOLO MAESTRO ELÉCTRICO
        </button>

        {/* Combo Button */}
        <button onClick={() => onChange('combo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight flex flex-col items-center justify-center gap-1 ${selected === 'combo' ? 'bg-black border-[#FFFF00] text-white animate-neon-yellow z-10' : 'bg-[#0a0a0a] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-68">
          {/* Embedded Badge */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFFF00] text-black text-[10px] font-black px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,255,0,0.4)] z-10 whitespace-nowrap uppercase tracking-widest animate-badge-glow" data-id="element-69">
            MÁS ELEGIDO
          </div>

          {/* Engraved Seal Icon */}
          <div className="absolute top-3 right-3 opacity-20 group-hover:opacity-40 transition-opacity" data-id="element-70">
            <Award className="w-8 h-8 md:w-10 md:h-10 text-[#FFFF00]" strokeWidth={1} data-id="element-71" />
          </div>

          <span data-id="element-72">COMBO MAESTRO ELÉCTRICO + PLC MASTER</span>
        </button>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  } = useDynamicStats();
  const {
    dateString: currentDate,
    shortDate,
    weekdayDate
  } = useCurrentDate();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<'solo' | 'combo'>('combo');
  const [floatingPrice, setFloatingPrice] = useState<{
    value: string;
    color: string;
    visible: boolean;
  }>({
    value: '',
    color: '',
    visible: false
  });
  const triggerFloatingPrice = (value: string, color: string) => {
    setFloatingPrice({
      value,
      color,
      visible: true
    });
    setTimeout(() => {
      setFloatingPrice(prev => ({
        ...prev,
        visible: false
      }));
    }, 1000);
  };
  const [fearsVisible, setFearsVisible] = useState(false);
  const [problemVisible, setProblemVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [localReview, setLocalReview] = useState<{
    name: string;
    text: string;
    stars: number;
  } | null>(null);
  const [bonusIndex, setBonusIndex] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const marqueeResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const isDragging = useRef(false);
  // Marquee pause/resume handlers
  const pauseMarquee = () => {
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Drag handlers for manual swipe
  const handleDragStart = (clientX: number) => {
    const container = marqueeContainerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragScrollLeft.current = container.scrollLeft;
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging.current) return;
    const container = marqueeContainerRef.current;
    if (!container) return;
    const dx = clientX - dragStartX.current;
    container.scrollLeft = dragScrollLeft.current - dx;
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Argentine chat reviews pool (30+)
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la forma de laburar. Antes conectaba de memoria y ahora entiendo qué hace cada cable 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano, nada de vueltas teóricas al pedo. Los diagramas están claritos',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con un cliente. Tenía que automatizar un tanque y no le encontraba la vuelta. Con esto lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso hermano. Solo con la planilla de presupuesto ya se paga solo. La uso para todos los laburos',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la cabeza mal. Antes adivinaba, ahora sé lo que hago ⚡',
    time: '16:22',
    verified: true
  }, {
    name: 'Ricardo A.',
    msg: 'Soy técnico hace 10 pirulos y siempre se aprende algo nuevo. El protocolo de verificación es un caño, lo uso siempre',
    time: '20:11',
    verified: true
  }, {
    name: 'Marcelo D.',
    msg: 'Al principio me costó un toque porque venía de cero, pero leyéndolo tranqui se entiende todo. Me salvó con un tablero de bombas',
    time: '08:45',
    verified: false
  }, {
    name: 'Fernando T.',
    msg: 'Es técnico pero se deja leer. No es esos libros aburridos de ingeniería que te dormís. Es para el que está en la obra posta',
    time: '13:20',
    verified: true
  }, {
    name: 'Jorge L.',
    msg: 'Lo imprimí y lo llevo en la caja de herramientas. Cuando tengo una duda lo miro ahí nomás. Re práctico',
    time: '17:55',
    verified: true
  }, {
    name: 'Sergio F.',
    msg: 'Está muy completo che. Estaría bueno que tenga videos, pero las explicaciones escritas son un golazo igual',
    time: '10:30',
    verified: true
  }, {
    name: 'Diego P.',
    msg: 'Ni hablar, lo mejor que compré en años. Ahora los tableros no me asustan más jaja',
    time: '15:12',
    verified: true
  }, {
    name: 'Ramiro V.',
    msg: 'Capo el que armó esto. Se nota que sabe de campo, no es un teórico de escritorio. Re recomendado 👌',
    time: '19:40',
    verified: true
  }, {
    name: 'Hernán G.',
    msg: 'Arranqué a tomar laburos más grandes gracias a esto. Antes les tenía miedo a los tableros industriales',
    time: '07:55',
    verified: true
  }, {
    name: 'Leandro M.',
    msg: 'Posta que vale la pena. Mi viejo es electricista y se lo pasé, quedó re manija también',
    time: '12:18',
    verified: false
  }, {
    name: 'Cristian R.',
    msg: 'Lo que más me sirvió fue entender la lógica de los contactores. Antes los conectaba y rezaba jaja',
    time: '21:05',
    verified: true
  }, {
    name: 'Facundo L.',
    msg: 'Che re completo. Los bonos solos ya valen más que lo que pagué. Un golazo la planilla de presupuesto',
    time: '14:50',
    verified: true
  }, {
    name: 'Nicolás S.',
    msg: 'Soy de Córdoba y acá hay mucho laburo industrial. Con esto me animé a meterme en automatización. Gracias crack 🙌',
    time: '16:33',
    verified: true
  }, {
    name: 'Martín H.',
    msg: 'Lo arranqué a leer un domingo y no lo solté. Está explicado como si te lo contara un compañero de laburo',
    time: '11:47',
    verified: true
  }, {
    name: 'Walter B.',
    msg: 'Tengo 15 años en el rubro y hay cosas que no sabía. El tema de protecciones me voló la cabeza',
    time: '09:22',
    verified: true
  }, {
    name: 'Carlos A.',
    msg: 'Muy bueno loco. Lo único que le falta son más ejemplos de variadores, pero para mando es lo mejor que vi',
    time: '18:15',
    verified: false
  }, {
    name: 'Ezequiel D.',
    msg: 'Me lo recomendó un colega y la verdad que tenía razón. Re sirve para el día a día en obra',
    time: '13:08',
    verified: true
  }, {
    name: 'Damián F.',
    msg: 'Che, el bono de paneles solares es tremendo. Estoy pensando en meterme en eso también. Todo suma 🔋',
    time: '20:30',
    verified: true
  }, {
    name: 'Lucas T.',
    msg: 'Posta que cambió mi forma de presupuestar. Antes tiraba números al voleo, ahora tengo todo ordenado',
    time: '15:42',
    verified: true
  }, {
    name: 'Alejandro N.',
    msg: 'Lo mejor es que lo podés consultar en la obra. Nada de andar buscando en YouTube a las apuradas',
    time: '08:10',
    verified: true
  }, {
    name: 'Tomás R.',
    msg: 'Soy de Mendoza, laburo en bodegas. Los circuitos de mando de las bombas ahora los entiendo de verdad',
    time: '17:25',
    verified: true
  }, {
    name: 'Ignacio C.',
    msg: 'Un amigo me dijo "compralo que no te vas a arrepentir" y tenía razón. Muy claro todo',
    time: '12:55',
    verified: false
  }, {
    name: 'Santiago M.',
    msg: 'Re piola el material. Se nota que está hecho por alguien que labura en serio, no un profesor de facultad',
    time: '19:18',
    verified: true
  }, {
    name: 'Agustín V.',
    msg: 'Che lo del protocolo de verificación es genial. Ahora antes de dar tensión reviso todo como corresponde',
    time: '10:05',
    verified: true
  }, {
    name: 'Maximiliano P.',
    msg: 'Increíble la relación precio-calidad. Con lo que sale una herramienta tenés conocimiento para toda la vida',
    time: '14:40',
    verified: true
  }, {
    name: 'Gonzalo E.',
    msg: 'Lo compré medio desconfiado y me sorprendió. Está muy bien armado, se nota el laburo que le metieron 💯',
    time: '16:58',
    verified: true
  }];
  const testimonialData = [{
    name: 'Juan P.',
    specialty: 'Instalador Eléctrico',
    study: 'Escuela Técnica N°1',
    comment: 'Lo que más me sirvió fue la parte de lógica de mando. Dejé de copiar esquemas y empecé a entender por qué se conecta cada cable.',
    image: 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp'
  }, {
    name: 'Sergio L.',
    specialty: 'Electricista Matriculado',
    study: 'COPIME',
    comment: 'La inversión se recupera en el primer trabajo que hacés con seguridad. Mis clientes notan la diferencia en mi criterio técnico.',
    image: 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp'
  }, {
    name: 'Roberto G.',
    specialty: 'Electricista Industrial',
    study: 'UTN (Univ. Tecnológica Nacional)',
    comment: 'El manual de control eléctrico me cambió la forma de encarar los tableros. Antes dudaba mucho, ahora entiendo la lógica de punta a punta.',
    image: 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp'
  }, {
    name: 'Miguel A.',
    specialty: 'Técnico en Automatización',
    study: 'Centro de Formación Profesional',
    comment: 'Muy recomendado para los que recién arrancan y para los que ya tienen años. Siempre se aprende algo nuevo con estos manuales.',
    image: 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp'
  }, {
    name: 'Carlos M.',
    specialty: 'Mantenimiento de Plantas',
    study: 'Instituto Técnico Superior',
    comment: 'Excelente material. Los diagramas son súper claros y me sirvieron para resolver una falla en un motor que me tenía loco.',
    image: 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp'
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    // Observer for sticky bottom bar
    const stickyObserver = new IntersectionObserver(([entry]) => {
      setShowStickyBar(entry.isIntersecting || entry.boundingClientRect.top < 0);
    }, {
      threshold: 0.1
    });
    const compraSection = document.getElementById('compra');
    if (compraSection) stickyObserver.observe(compraSection);
    return () => {
      revealObserver.disconnect();
      stickyObserver.disconnect();
    };
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'fears-section') setFearsVisible(true);
          if (entry.target.id === 'problem-section') setProblemVisible(true);
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['fears-section', 'problem-section', 'problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47885775995102:1';
  };
  const handleComboPurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    setLocalReview(review);
  };
  const fearItems = [{
    icon: Flame,
    title: 'Tenés miedo de quemar un motor',
    desc: 'Un error de conexión y el equipo queda inutilizado.'
  }, {
    icon: AlertCircle,
    title: 'Cuando no responde',
    desc: 'Dás tensión… y el circuito no hace lo que debería.'
  }, {
    icon: Brain,
    title: 'La inseguridad te hace quedar mal frente al cliente',
    desc: 'No por el trabajo, sino por cómo lo enfrentás.'
  }, {
    icon: DollarSign,
    title: 'Estás estancado ganando siempre lo mismo',
    desc: 'porque te falta confianza para aceptar trabajos más grandes.'
  }];
  const problemItems = [{
    icon: BookX,
    title: 'Libros teóricos',
    desc: 'Mucho concepto, poca aplicación real.'
  }, {
    icon: Video,
    title: 'Videos aislados',
    desc: 'Sirven para un caso puntual, no cuando algo cambia.'
  }, {
    icon: HardHat,
    title: 'Experiencia sola',
    desc: 'Funciona… hasta que aparece un caso distinto.'
  }];
  const decisionItems = [{
    icon: CircuitBoard,
    text: 'Criterio claro para interpretar circuitos de mando',
    keyword: 'Criterio'
  }, {
    icon: Cpu,
    text: 'Lógica aplicada a motores y tableros reales',
    keyword: 'Lógica'
  }, {
    icon: Target,
    text: 'Menos dudas al montar y diagnosticar',
    keyword: 'diagnóstico'
  }, {
    icon: Settings,
    text: 'Decisiones técnicas, no intuición',
    keyword: 'Decisiones'
  }, {
    icon: Award,
    text: 'Material pensado para trabajo en campo',
    keyword: 'campo'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-73">
      {notification && !headerVisible && <PurchaseNotification name={notification.name} city={notification.city} pack={notification.pack} isVisible={isVisible} data-id="element-74" />}

      {/* Header - Hidden initially, appears on scroll */}
      <motion.header animate={{
      paddingTop: isVisible && notification ? '20px' : '12px',
      paddingBottom: isVisible && notification ? '20px' : '12px',
      backgroundColor: isVisible && notification ? '#1e3a8a' : '#172554',
      boxShadow: isVisible && notification ? '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }} className={`text-white fixed top-0 left-0 right-0 z-40 transition-all duration-400 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'}`} data-id="element-75">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-76">
          <div className="flex items-center gap-3 overflow-hidden" data-id="element-77">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse flex-shrink-0" data-id="element-78">
              <img src="https://cdn.magicpatterns.com/uploads/tA6D5wwQNzXrTuuBrNPdzP/capture_260206_165744.png" alt="Maestro Eléctrico Logo" className="w-full h-full object-cover" loading="lazy" decoding="async" width={40} height={40} data-id="element-79" />
            </div>

            <div className="relative h-10 flex items-center min-w-[200px]" data-id="element-80">
              <AnimatePresence mode="wait" data-id="element-81">
                {isVisible && notification ? <motion.div key={`${notification.name}-${notification.city}`} initial={{
                y: 20,
                opacity: 0,
                scale: 0.9
              }} animate={{
                y: 0,
                opacity: 1,
                scale: 1
              }} exit={{
                y: -20,
                opacity: 0,
                scale: 0.9
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25
              }} className="flex items-center gap-2" data-id="element-82">
                    <div className="bg-green-500 p-1.5 rounded-full flex-shrink-0 shadow-lg shadow-green-500/20" data-id="element-83">
                      {notification.pack === 'solo' ? <Zap className="w-4 h-4 text-white fill-white" data-id="element-84" /> : <Flame className="w-4 h-4 text-white fill-white" data-id="element-85" />}
                    </div>
                    <div className="flex flex-col justify-center" data-id="element-86">
                      <p className="text-[11px] md:text-[12px] font-black leading-tight tracking-tight" data-id="element-87">
                        <span className="text-green-400" data-id="element-88">
                          {notification.name}
                        </span>{' '}
                        de {notification.city}
                      </p>
                      <p className="text-[9px] md:text-[10px] text-blue-100 font-bold uppercase tracking-wider" data-id="element-89">
                        compró{' '}
                        <span className="text-white" data-id="element-90">
                          {notification.pack === 'solo' ? 'Maestro Eléctrico' : 'Combo Maestro + PLC'}
                        </span>
                      </p>
                    </div>
                  </motion.div> : <motion.span key="default-text" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="font-bold tracking-tight text-[14px] text-blue-100/50" data-id="element-91">
                    Inscripciones abiertas
                  </motion.span>}
              </AnimatePresence>
            </div>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded transition-colors text-[14px] flex-shrink-0 pl-[5px] pr-[5px]" data-id="element-92">
            Ver Oferta
          </button>
        </div>
      </motion.header>

      {/* TOP HERO IMAGE BANNER */}
      <div className="w-full leading-none" data-id="element-93"></div>

      {/* HERO - Radial gradient with vibrant blue center */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-8" style={{
      background: 'radial-gradient(ellipse at 50% 50%, #1e40af 0%, #1e3a8a 25%, #172554 50%, #0f1729 80%, #0a1120 100%)'
    }} data-id="element-94">
        <div className="container mx-auto px-4 relative z-10" data-id="element-95">
          {/* Small text above title */}
          <p className="text-center text-[11px] md:text-xs font-medium text-blue-300/70 uppercase tracking-[0.25em] mb-6 animate-hero-text" style={{
          animationDelay: '0s'
        }} data-id="element-96">
            PARA ELECTRICISTAS, TÉCNICOS Y MANTENIMIENTO INDUSTRIAL
          </p>

          {/* Main Title - Staggered fade */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-[1.1] text-center" data-id="element-97">
            <span className="block animate-hero-text text-white" style={{
            animationDelay: '0.1s'
          }} data-id="element-98">
              CONVERTITE
            </span>
            <span className="block animate-hero-text text-yellow-400" style={{
            animationDelay: '0.3s'
          }} data-id="element-99">
              EN UN MAESTRO ELÉCTRICO
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-center mb-12 font-medium animate-hero-text text-blue-100" style={{
          animationDelay: '0.5s'
        }} data-id="element-100">
            ENTENDÉ EL CONTROL ELÉCTRICO Y TOMÁ{' '}
            <span className="text-yellow-300" data-id="element-101">TRABAJOS MEJORES PAGOS</span>
          </p>

          {/* Mockup - Premium with glow */}
          <div className="mb-10 flex justify-center relative animate-hero-zoom" style={{
          animationDelay: '0.7s'
        }} data-id="element-102">
            {/* Glow behind mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-blue-500/30 rounded-full blur-[80px]" data-id="element-103"></div>
            <img src="https://cdn.magicpatterns.com/uploads/fMZ5Fq5SNbxYwzfww2mdPS/Diseno_sin_titulo_(1).webp" alt="Control Eléctrico Libro Digital" className="w-full max-w-xs md:max-w-sm drop-shadow-2xl cursor-pointer relative z-10 hover:scale-[1.02] transition-transform duration-700" onClick={scrollToPurchase} decoding="async" width={384} height={448} data-id="element-104" />
          </div>

          {/* Stars + Social proof - Smaller */}
          <div className="text-center mb-8 animate-hero-text" style={{
          animationDelay: '0.9s'
        }} data-id="element-105">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-106">
              <div className="flex items-center gap-0.5" data-id="element-107">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-108" />)}
              </div>
              <span className="text-xs font-medium text-blue-300 uppercase tracking-wide" data-id="element-109">
                (4.9/5)
              </span>
            </div>
            <p className="text-xs font-medium text-blue-300 uppercase tracking-wide" data-id="element-110">
              Más de 6.250 profesionales ya lo usan
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-hero-text" style={{
          animationDelay: '1.1s'
        }} data-id="element-111">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-2xl font-bold py-4 px-10 rounded-lg shadow-lg transition-all inline-flex items-center gap-2 animate-cta-pulse hover:scale-105" data-id="element-112">
              EMPEZAR HOY MISMO ↓
            </button>
            <p className="mt-4 md:text-xs text-blue-300/50 uppercase tracking-widest animate-hero-text text-[8px]" style={{
            animationDelay: '1.3s'
          }} data-id="element-113">
              (descarga inmediata ∙ acceso permanente ∙ sin mensualidad)
            </p>
          </div>
        </div>
      </section>

      {/* POST-HERO - Antes vs Después Comparison */}
      <section id="post-hero-section" className="py-6 px-4" style={{
      background: '#0a1929'
    }} data-id="element-114">
        <div className="container mx-auto max-w-3xl" data-id="element-115">
          <div data-reveal className={`relative bg-gradient-to-br from-gray-800/90 to-blue-900/60 border border-yellow-500/30 rounded-2xl p-5 md:p-8 shadow-2xl overflow-hidden ${postHeroVisible ? '' : ''}`} data-id="element-116">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" data-id="element-117"></div>

            {/* Comparison Table */}
            <div className="grid grid-cols-2 gap-0 mb-6" data-id="element-118">
              {/* Header */}
              <div className="bg-red-900/40 border-b border-r border-gray-600/30 p-3 text-center rounded-tl-lg" data-id="element-119">
                <p className="text-sm font-bold text-red-300 uppercase tracking-wide" data-id="element-120">
                  Instalador Común
                </p>
              </div>
              <div className="bg-yellow-600/20 border-b border-gray-600/30 p-3 text-center rounded-tr-lg" data-id="element-121">
                <p className="text-sm font-bold text-yellow-400 uppercase tracking-wide" data-id="element-122">
                  Maestro Eléctrico
                </p>
              </div>

              {/* Rows */}
              {[{
              left: 'Conecta cables por memoria',
              right: 'Entiende la lógica del circuito'
            }, {
              left: 'Copia diagramas sin comprenderlos',
              right: 'Lee e interpreta cualquier diagrama'
            }, {
              left: 'Se traba cuando algo cambia',
              right: 'Resuelve variantes con criterio'
            }, {
              left: 'Cobra lo mismo hace años',
              right: 'Toma trabajos más grandes y mejor pagos'
            }, {
              left: 'Depende de otro para validar',
              right: 'Tiene autoridad técnica frente al cliente'
            }].map((row, i) => <Fragment key={i} data-id="element-123">
                  <div className={`border-r border-gray-600/30 p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-124">
                    <div className="flex items-start gap-2" data-id="element-125">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-126" />
                      <p className="text-sm text-gray-400" data-id="element-127">{row.left}</p>
                    </div>
                  </div>
                  <div className={`p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-128">
                    <div className="flex items-start gap-2" data-id="element-129">
                      <Check className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-130" />
                      <p className="text-sm text-white font-medium" data-id="element-131">
                        {row.right}
                      </p>
                    </div>
                  </div>
                </Fragment>)}
            </div>

            {/* Closing statement */}
            <div className="text-center pt-4 border-t border-gray-700/50 flex flex-col items-center gap-3" data-id="element-132">
              <Lightbulb className="w-8 h-8 text-yellow-400 animate-bulb-glow" strokeWidth={1.5} data-id="element-133" />
              <p className="text-xl md:text-2xl font-extrabold text-yellow-400 leading-tight uppercase tracking-wide drop-shadow-lg" data-id="element-134">
                UN MAESTRO ELÉCTRICO RESUELVE EL CIRCUITO ANTES DE TOCARLO.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - With animated background */}
      <section id="problem-real-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-135">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-136"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-137">
          <h3 className={`text-3xl md:text-4xl font-extrabold text-white mb-10 text-center uppercase tracking-wide transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-138">
            EL <span className="text-yellow-400" data-id="element-139">PROBLEMA</span> REAL
          </h3>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm border-l-4 border-red-500 rounded-r-xl p-6 md:p-10 shadow-lg transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-140">
            <p className="text-lg md:text-xl text-gray-300 mb-8 font-light" data-id="element-141">
              Sabés conectar componentes individuales.
            </p>

            <div className="bg-gray-700/40 border border-gray-600/50 p-6 rounded-lg mb-10 relative overflow-hidden" data-id="element-142">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500/50" data-id="element-143"></div>
              <div className="flex gap-4" data-id="element-144">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" data-id="element-145" />
                <p className="text-xl md:text-2xl font-bold text-white leading-relaxed" data-id="element-146">
                  Pero el problema aparece cuando no terminás de entender la
                  lógica completa del circuito.
                </p>
              </div>
            </div>

            <div className="space-y-6 mb-10" data-id="element-147">
              {['Conocés contactores, térmicos y pulsadores por separado.', 'La dificultad real surge al integrarlos en un sistema.', 'Esa falta de visión global genera errores y retrabajo.'].map((text, i) => <div key={i} className={`flex items-start gap-4 transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`
            }} data-id="element-148">
                  <div className="w-6 h-6 rounded-full border border-gray-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-149">
                    <div className="w-2 h-2 bg-red-400 rounded-full" data-id="element-150"></div>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed" data-id="element-151">
                    {text}
                  </p>
                </div>)}
            </div>

            <div className="pt-8 border-t border-gray-700/50" data-id="element-152">
              <p className="text-lg font-medium text-gray-400 text-center italic opacity-90" data-id="element-153">
                "El problema no es falta de capacidad. Es que casi nadie enseña
                a entender la lógica real del control eléctrico."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ¿TE PASA ESTO? - With animated background */}
      <section id="fears-section" className="py-8 px-4 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 animate-gradient-slow relative overflow-hidden" data-id="element-154">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-155"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-156">
          <div className={`text-center mb-12 transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-157">
            <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-4 uppercase" data-id="element-158">
              ¿TE <span className="text-yellow-400" data-id="element-159">PASA</span> ESTO?
            </h3>
            <p className="text-lg text-gray-400" data-id="element-160">
              Si trabajás con tableros, motores y circuitos, esto es para vos.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-161">
            <div className="space-y-6" data-id="element-162">
              {fearItems.map((item, i) => <div key={i} className={`flex items-start gap-4 pb-6 ${i < fearItems.length - 1 ? 'border-b border-gray-700' : ''} transition-all duration-500 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-163">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-164">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-165" />
                  </div>
                  <div data-id="element-166">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-167">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-168">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-169">
              <p className="text-xl font-medium text-gray-300 mb-2" data-id="element-170">
                Ahí aparece la inseguridad.
              </p>
              <p className="text-xl font-bold text-white" data-id="element-171">
                Y en control eléctrico, la inseguridad{' '}
                <span className="text-yellow-400" data-id="element-172">se paga caro</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA NO SOS VOS - With animated background */}
      <section id="problem-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-173">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-174"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-175">
          <div className={`text-center mb-10 transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-176">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-2 uppercase" data-id="element-177">
              EL PROBLEMA <span className="text-yellow-400" data-id="element-178">NO SOS VOS</span>
            </h3>
            <p className="text-lg text-gray-400" data-id="element-179">
              La mayoría aprende partes sueltas, pero no el sistema completo.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-180">
            <div className="space-y-5" data-id="element-181">
              {problemItems.map((item, i) => <div key={i} className={`flex items-start gap-4 ${i < problemItems.length - 1 ? 'pb-5 border-b border-gray-700' : ''} transition-all duration-500 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-182">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-183">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-184" />
                  </div>
                  <div data-id="element-185">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-186">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-187">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-188">
              <p className="text-lg font-bold text-white" data-id="element-189">
                En casi ningún lado te enseñan a pensar el control eléctrico
                como un{' '}
                <span className="text-yellow-400" data-id="element-190">sistema completo</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="py-8 px-4 bg-gradient-to-b from-blue-900 to-blue-800 text-white relative overflow-hidden" data-id="element-191">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-192"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-193">
          <div className="text-center mb-12" data-id="element-194">
            <span className="bg-yellow-500 text-blue-900 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-195">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 uppercase" data-id="element-196">
              AHÍ NACE{' '}
              <span className="text-yellow-400" data-id="element-197">MAESTRO ELÉCTRICO</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto" data-id="element-198">
              Un manual práctico creado para ayudarte a:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-199">
            <div className="space-y-4" data-id="element-200">
              {['Entender la lógica de los circuitos de mando', 'Leer diagramas con seguridad', 'Aplicar lo aprendido en motores y tableros reales', 'Dejar de memorizar esquemas y empezar a comprenderlos'].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-800/50 p-4 rounded-lg border border-blue-700" data-id="element-201">
                  <Check className="text-yellow-400 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-202" />
                  <span className="text-lg" data-id="element-203">{item}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-204">
              <video src="https://cdn.magicpatterns.com/uploads/rHCx25VPdb8sHH7a3Qn4xF/0312 (1)(1).mp4" className="w-full max-w-sm drop-shadow-2xl rounded-lg" autoPlay loop muted playsInline data-id="element-205" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center max-w-3xl mx-auto" data-id="element-206">
            <p className="text-xl italic leading-relaxed" data-id="element-207">
              "Este material funciona como un traductor entre el diagrama y el
              tablero real."
            </p>
          </div>
        </div>
      </section>

      {/* DECISION BLOCK */}
      <section id="decision-section" className="py-8 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden" data-id="element-208">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" data-id="element-209"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-210">
          <div className="text-center mb-12" data-id="element-211">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight uppercase" data-id="element-212">
              <span className="text-yellow-400" data-id="element-213">CONTROL ELÉCTRICO</span>
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-214">
              EL CRITERIO QUE TE FALTABA EN TABLEROS Y MOTORES
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-12" data-id="element-215">
            <div className="space-y-3" data-id="element-216">
              {decisionItems.map((item, index) => <div key={index} className={`flex items-center gap-4 p-4 rounded-lg bg-blue-800/40 border border-blue-600/40 transition-all duration-300 hover:bg-blue-700/50 hover:border-blue-500/50 hover:translate-y-[-2px] hover:shadow-lg ${decisionVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${index * 80}ms`,
              animationFillMode: 'forwards'
            }} data-id="element-217">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="element-218">
                    <item.icon className="w-5 h-5 text-yellow-400" data-id="element-219" />
                  </div>
                  <span className="text-lg text-gray-100" data-id="element-220">
                    {item.text.split(item.keyword).map((part, i, arr) => <span key={i} data-id="element-221">
                        {part}
                        {i < arr.length - 1 && <span className="text-blue-300" data-id="element-222">{item.keyword}</span>}
                      </span>)}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="text-center" data-id="element-223">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-2xl font-extrabold py-5 px-12 rounded-lg shadow-2xl hover:shadow-yellow-500/30 transition-all transform hover:scale-105" data-id="element-224">
              SÍ, QUIERO LA OFERTA AHORA
            </button>
          </div>
        </div>
      </section>

      {/* BONUSES - Carousel */}
      <section id="bonuses" className="py-8 px-4 bg-[#00005E]" data-id="element-225">
        <div className="container mx-auto max-w-6xl" data-id="element-226">
          <div className="text-center mb-12" data-reveal data-id="element-227">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight relative inline-block" data-id="element-228">
              SI ACCEDÉS HOY, TE LLEVÁS ESTOS 5 BONUS DE REGALO
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" data-id="element-229"></span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 mt-4" data-id="element-230">
              Complementos perfectos para tu crecimiento profesional
            </p>
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-400 px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide border border-yellow-500/30" data-id="element-231">
              <Clock className="w-4 h-4" data-id="element-232" />
              GRATIS POR TIEMPO LIMITADO
            </div>
          </div>

          {/* CSS Animation Auto-Scroll Carousel with drag support */}
          <div ref={marqueeContainerRef} className={`cursor-grab active:cursor-grabbing ${marqueePaused ? 'overflow-x-auto' : 'overflow-hidden'}`} style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} onMouseDown={e => handleDragStart(e.clientX)} onMouseMove={e => handleDragMove(e.clientX)} onMouseUp={handleDragEnd} onMouseLeave={handleDragEnd} onTouchStart={e => handleDragStart(e.touches[0].clientX)} onTouchMove={e => handleDragMove(e.touches[0].clientX)} onTouchEnd={handleDragEnd} data-id="element-233">
            <div className={`bonus-marquee-track flex gap-6 ${marqueePaused ? 'bonus-marquee-paused' : ''}`} data-id="element-234">
              {/* Render bonuses twice for seamless infinite loop */}
              {[...Array(2)].map((_, setIdx) => [{
              title: 'CURSO DE PANELES SOLARES',
              desc: 'Más de 50 clases grabadas para sumar una nueva especialidad altamente demandada en energía solar.',
              img: 'https://cdn.magicpatterns.com/uploads/w2rTPZ1b9h5D7JWz5dRRda/usa-la-imagen-adjunta-como-referencia-vi_LIZOyjwkQ0eu3ZbUzqbGFw_459-daMfTbW7XMmyjjHVYA_2k.png',
              value: '$32.500 ARS',
              exclusive: true,
              label: 'BONO EXCLUSIVO'
            }, {
              title: 'PLANTILLAS DE PRESUPUESTO EDITABLE',
              desc: 'Herramienta editable diseñada para trabajos eléctricos profesionales.',
              img: 'https://cdn.magicpatterns.com/uploads/52EEtPjWC34BYh7Nk8wekR/Prompt__A_high-end,_202604151035.webp',
              value: '$6.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }, {
              title: 'PROTOCOLO DE VERIFICACIÓN PREVIA A LA ENERGIZACIÓN',
              desc: 'Documento técnico estructurado que establece criterios de verificación.',
              img: 'https://cdn.magicpatterns.com/uploads/ecDoD4jMmyVG3yjoo5c8Tn/Prompt__A_detailed,_202604151040.webp',
              value: '$9.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }, {
              title: 'EFICIENCIA EN USO INDUSTRIAL DE MOTORES ELÉCTRICOS',
              desc: 'Material técnico orientado al uso correcto de motores eléctricos en entornos industriales.',
              img: 'https://cdn.magicpatterns.com/uploads/nH726tHAQweq2rPqiPh3zm/A_detailed,_premium_202604151030.webp',
              value: '$14.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }, {
              title: 'REGLAMENTACIONES AEA - ASOCIACIÓN ELECTROTÉCNICA ARGENTINA',
              desc: 'Manual esencial sobre las normativas vigentes de la AEA para instalaciones eléctricas seguras y profesionales.',
              img: 'https://cdn.magicpatterns.com/uploads/xz24GCFVSGY7FVuNDfknEw/necesito_hacer_el_202604151258.jpg',
              value: '$12.500 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }].map((bonus, i) => <div key={`${setIdx}-${i}`} onClick={scrollToPurchase} className="cursor-pointer flex-shrink-0 w-[280px] md:w-[300px]" data-id="element-235">
                    <div className={`bg-gray-950 rounded-xl overflow-hidden border-2 h-full flex flex-col ${bonus.exclusive ? 'border-yellow-400 shadow-xl shadow-yellow-500/10' : 'border-yellow-600/50 shadow-lg'}`} data-id="element-236">
                      <div className={`${bonus.exclusive ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' : 'bg-gradient-to-r from-yellow-700 to-yellow-600 text-black'} px-4 py-2 text-center font-bold text-sm uppercase`} data-id="element-237">
                        {bonus.label}
                      </div>
                      <div className="p-5 flex flex-col flex-grow" data-id="element-238">
                        <div className="aspect-[3/4] mb-4 bg-black rounded-lg overflow-hidden relative" data-id="element-239">
                          {bonus.exclusive && <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded z-10 tracking-wider" data-id="element-240">
                              BONO PREMIUM
                            </div>}
                          <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={300} height={400} data-id="element-241" />
                        </div>
                        <h3 className={`font-bold text-sm ${bonus.exclusive ? 'text-yellow-400' : 'text-white'} mb-3 leading-tight uppercase`} data-id="element-242">
                          {bonus.title}
                        </h3>
                        <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow" data-id="element-243">
                          {bonus.desc}
                        </p>
                        <p className="text-sm text-gray-500 mb-2" data-id="element-244">
                          Valor:{' '}
                          <span className={`line-through ${bonus.exclusive ? 'text-red-400 font-medium' : 'text-gray-500'}`} data-id="element-245">
                            {bonus.value}
                          </span>
                        </p>
                        <div className={`${bonus.exclusive ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg px-3 py-2 mt-auto`} data-id="element-246">
                          <p className={`text-sm font-bold ${bonus.exclusive ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-247">
                            ✓ Incluido sin costo adicional
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>))}
            </div>
          </div>
          <p className="text-center text-sm mt-3" data-id="element-248">
            <span className="text-yellow-400" data-id="element-249">←</span>
            <span className="text-gray-400 mx-2" data-id="element-250">
              DESLIZÁ PARA VER MÁS BONOS
            </span>
            <span className="text-yellow-400" data-id="element-251">→</span>
          </p>
        </div>
      </section>

      {/* EXTRA BONUS - CERTIFICATE */}
      <section className="py-8 px-4 bg-gradient-to-b from-[#0a1a3a] to-[#061229] relative overflow-hidden" data-id="element-252">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-253"></div>
        <div className="container mx-auto max-w-2xl relative z-10" data-id="element-254">
          <div className="flex flex-col items-center text-center" data-id="element-255">
            {/* Certificate Image at the top with frame */}
            <div className="relative group w-full mb-6" data-id="element-256">
              <div className="absolute -inset-1 bg-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-257"></div>
              <div className="relative bg-[#1a2b4a] border-[6px] border-[#2a3b5a] p-2 md:p-4 rounded-xl shadow-2xl overflow-hidden" data-id="element-258">
                <img src="https://cdn.magicpatterns.com/uploads/wgzBvYCj2MeubudywG2qNn/Diseno_sin_titulo_(2).png" alt="Certificado Maestro Eléctrico" className="w-full h-auto rounded-sm" loading="lazy" decoding="async" data-id="element-259" />
              </div>
            </div>

            {/* Badge */}
            <span className="bg-yellow-500 text-blue-900 font-black px-3 py-1 rounded text-[10px] uppercase tracking-widest mb-4 shadow-lg" data-id="element-260">
              BONO ESPECIAL
            </span>

            {/* Title */}
            <h2 className="text-xl md:text-3xl font-extrabold text-white mb-3 leading-tight uppercase tracking-tight" data-id="element-261">
              Certificado Profesional en <br className="hidden md:block" data-id="element-262" />
              Comandos Eléctricos
            </h2>

            {/* Description */}
            <p className="text-xs md:text-sm text-blue-100 max-w-xl mb-6 leading-relaxed opacity-80" data-id="element-263">
              Al completar la guía, recibís un certificado digital de alta
              calidad listo para compartir con tus clientes y en redes sociales.
              Incluye una guía de una página sobre cómo usarlo para generar
              confianza y conseguir más trabajos.
            </p>

            {/* Included Gratis Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-900/40 border border-blue-400/30 px-4 py-1.5 rounded-full" data-id="element-264">
              <Check className="w-3.5 h-3.5 text-blue-400 stroke-[3px]" data-id="element-265" />
              <span className="text-blue-100 font-bold uppercase tracking-wider text-[10px]" data-id="element-266">
                Incluido Gratis con tu compra
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section id="benefits-section" className="py-8 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-267">
        <div className="container mx-auto max-w-5xl" data-id="element-268">
          <h2 className="md:text-4xl text-blue-900 mb-10 text-[28px] font-[700] font-[Poppins,_sans-serif]" data-id="element-269">
            LO QUE TE LLEVÁS CON MAESTRO ELÉCTRICO
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-270">
            {[{
            icon: Shield,
            title: 'CRITERIO Y SEGURIDAD',
            items: ['Más seguridad frente a los diagramas', 'Más criterio técnico'],
            tag: 'BASE TÉCNICA'
          }, {
            icon: Target,
            title: 'MENOS ERRORES',
            items: ['Menos errores', 'Menos dudas'],
            tag: 'MENOS RETRABAJO'
          }, {
            icon: TrendingUp,
            title: 'PERFIL PROFESIONAL',
            items: ['Más confianza profesional'],
            tag: 'MEJOR IMAGEN ANTE EL CLIENTE'
          }].map((card, i) => <div key={i} className={`bg-white border border-blue-200 rounded-xl p-6 shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${i * 100}ms`,
            animationFillMode: 'forwards'
          }} data-id="element-271">
                <div className="flex items-center gap-2 mb-4" data-id="element-272">
                  <card.icon className="w-5 h-5 text-blue-600" data-id="element-273" />
                  <h3 className="font-bold text-gray-900" data-id="element-274">{card.title}</h3>
                </div>
                <div className="space-y-3 mb-4" data-id="element-275">
                  {card.items.map((item, j) => <div key={j} className="flex items-start gap-2" data-id="element-276">
                      <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-277" />
                      <span className="text-gray-700 text-sm" data-id="element-278">{item}</span>
                    </div>)}
                </div>
                <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full" data-id="element-279">
                  {card.tag}
                </span>
              </div>)}
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-8 rounded-xl text-center shadow-lg" data-id="element-280">
            <p className="text-xl font-semibold leading-relaxed font-[Poppins,_sans-serif]" data-id="element-281">
              Invertís una vez en criterio. Eso se traduce en más trabajos,
              mejores decisiones y clientes que confían en vos.
            </p>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF CAROUSEL */}
      <section className="py-12 px-4 bg-gray-100 overflow-hidden" data-id="element-282">
        <div className="container mx-auto max-w-6xl" data-id="element-283">
          <div className="text-center mb-10" data-id="element-284">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-[Poppins,_sans-serif]" data-id="element-285">
              LO QUE DICEN OTROS PROFESIONALES
            </h2>
            <p className="text-gray-600" data-id="element-286">
              Más de 6.250 electricistas ya están aplicando este criterio
              técnico
            </p>
          </div>

          <div className="relative" data-id="element-287">
            <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar" data-id="element-288">
              {testimonialData.map((t, i) => <div key={i} className="flex-shrink-0 w-[300px] md:w-[400px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden snap-center flex flex-col" data-id="element-289">
                  <div className="aspect-square overflow-hidden relative" data-id="element-290">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={1080} height={1080} data-id="element-291" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4" data-id="element-292">
                      <p className="text-white font-bold text-lg" data-id="element-293">{t.name}</p>
                      <p className="text-blue-300 text-sm font-medium" data-id="element-294">
                        {t.specialty}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-295">
                    <div className="flex items-center gap-0.5 mb-4" data-id="element-296">
                      {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-297" />)}
                    </div>
                    <p className="text-gray-700 italic mb-6 flex-grow" data-id="element-298">
                      "{t.comment}"
                    </p>
                    <div className="pt-4 border-t border-gray-100" data-id="element-299">
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1" data-id="element-300">
                        Estudió en:
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-301">
                        {t.study}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Visual indicators for mobile */}
            <div className="flex justify-center gap-2 mt-2 md:hidden" data-id="element-302">
              {testimonialData.map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" data-id="element-303"></div>)}
            </div>

            <p className="text-center text-sm text-gray-400 mt-4 flex items-center justify-center gap-2" data-id="element-304">
              <ChevronLeft className="w-4 h-4" data-id="element-305" /> Deslizá para ver más{' '}
              <ChevronRight className="w-4 h-4" data-id="element-306" />
            </p>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION - TWO CARDS */}
      <section id="compra" className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-307">
        <div className="container mx-auto max-w-2xl" data-id="element-308">
          <div className="text-center mb-6" data-id="element-309">
            <h2 className="text-lg md:text-xl font-black text-gray-900 uppercase tracking-tighter flex items-center justify-center gap-2" data-id="element-310">
              SELECCIONÁ TU NIVEL DE CAPACITACIÓN AQUÍ 👇
            </h2>
          </div>

          <OfferSelector selected={selectedOffer} onChange={setSelectedOffer} data-id="element-311" />

          <AnimatePresence mode="wait" data-id="element-312">
            {selectedOffer === 'solo' ? <motion.div key="solo" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-313">
                {/* CARD 1: MAESTRO ELÉCTRICO — Deep Industrial Blue + Glow */}
                <div className="bg-[#0a1a3a] rounded-2xl border-[3px] border-blue-400/70 relative overflow-hidden shadow-2xl animate-blue-glow-pulse" data-id="element-314">
                  {/* Blue Banner — Updated text */}
                  <div className="bg-blue-600 text-white font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" data-id="element-315">
                    OFERTA POR TIEMPO LIMITADO
                  </div>

                  <div className="p-6 md:p-8" data-id="element-316">
                    {/* Video */}
                    <div className="mb-6 rounded-lg overflow-hidden shadow-md border border-blue-500/30" data-id="element-317">
                      <video src="https://cdn.magicpatterns.com/uploads/5LmA44anJeCQ6Uq6aCcidD/Diseno_sin_titulo_(9).undefined" className="w-full h-auto" autoPlay loop muted playsInline data-id="element-318" />
                    </div>

                    {/* NEW TITLE — Inserted between video and price */}
                    <h3 className="text-2xl md:text-3xl font-extrabold text-center uppercase tracking-wide mb-6 text-white" data-id="element-319">
                      MAESTRO ELÉCTRICO + 5 BONOS DE REGALO
                    </h3>

                    {/* Pricing block — adjusted for dark bg */}
                    <div className="text-center mb-1" data-id="element-320">
                      <p className="text-sm text-gray-400 font-medium" data-id="element-321">
                        Antes{' '}
                        <span className="line-through text-gray-500" data-id="element-322">
                          $49.900 ARS
                        </span>
                      </p>
                    </div>
                    <div className="text-center mb-5" data-id="element-323">
                      <p className="text-5xl md:text-6xl font-extrabold text-white leading-none" data-id="element-324">
                        $19.990{' '}
                        <span className="text-lg font-normal text-gray-400" data-id="element-325">
                          ARS
                        </span>
                      </p>
                    </div>

                    {/* Installments badge */}
                    <div className="flex justify-center mb-6" data-id="element-326">
                      <div className="badge-shimmer bg-green-900/40 text-green-400 font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border border-green-500/30" data-id="element-327">
                        <CreditCard className="w-4 h-4" data-id="element-328" />
                        Hasta 3 cuotas sin interés
                      </div>
                    </div>

                    {/* Urgency Header — Compact black bar */}
                    <div className="bg-black rounded-lg px-4 py-2 mb-3 text-center border border-yellow-500/60" data-id="element-329">
                      <p className="text-xs md:text-sm font-bold text-white uppercase tracking-wide" data-id="element-330">
                        OFERTA VÁLIDA SOLO POR HOY:{' '}
                        <span className="text-yellow-400 font-extrabold" data-id="element-331">
                          {weekdayDate}
                        </span>
                      </p>
                    </div>

                    {/* Urgency Counters Row — viewers + cupos */}
                    <div className="bg-black rounded-xl px-4 py-3 mb-3 border border-gray-800 flex items-center justify-center gap-4" data-id="element-332">
                      <div className="flex items-center gap-2" data-id="element-333">
                        <Eye className="w-4 h-4 text-gray-400" data-id="element-334" />
                        <span className="text-lg font-bold" style={{
                      color: '#00FF00',
                      textShadow: '0 0 8px rgba(0,255,0,0.6), 0 0 20px rgba(0,255,0,0.3)'
                    }} data-id="element-335">
                          {viewers}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-336">
                          Personas mirando
                          <br data-id="element-337" />
                          en este momento
                        </span>
                      </div>
                      <div className="w-px h-8 bg-gray-700" data-id="element-338"></div>
                      <div className="flex items-center gap-2" data-id="element-339">
                        <Ticket className="w-4 h-4 text-gray-400" data-id="element-340" />
                        <span className="text-lg font-bold animate-red-glow-pulse" style={{
                      color: '#FF0000',
                      textShadow: '0 0 8px rgba(255,0,0,0.6), 0 0 20px rgba(255,0,0,0.3)'
                    }} data-id="element-341">
                          {urgencySpots}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-342">
                          Cupos
                          <br data-id="element-343" />
                          disponibles
                        </span>
                      </div>
                    </div>

                    {/* Professional Counter — Dark style, green glow */}
                    <div className="bg-black rounded-xl px-6 py-4 mb-6 border border-gray-800" data-id="element-344">
                      <div className="flex flex-col items-center" data-id="element-345">
                        <span className="text-4xl font-extrabold tracking-tight" style={{
                      color: '#00FF00',
                      textShadow: '0 0 10px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.3), 0 0 50px rgba(0,255,0,0.15)'
                    }} data-id="element-346">
                          {mainBuyers}
                        </span>
                        <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em] mt-1" data-id="element-347">
                          PROFESIONALES YA ACCEDIERON
                        </span>
                      </div>
                    </div>

                    {/* Inventory List - 5 Points */}
                    <div className="space-y-3 mb-8 bg-black/30 p-5 rounded-xl border border-white/5" data-id="element-348">
                      {['Manual Maestro Eléctrico (Digital)', 'Bono 1: Curso de Paneles Solares', 'Bono 2: Plantillas de Presupuesto', 'Bono 3: Eficiencia en Motores', 'Bono 4: Protocolo de Verificación', 'Bono 5: Reglamentaciones AEA', 'EXTRA: Certificado de Finalización'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-349">
                          <Check className="w-5 h-5 text-[#FFFF00] flex-shrink-0 mt-0.5" data-id="element-350" />
                          <span className="text-gray-200 font-medium text-sm md:text-base" data-id="element-351">
                            {item}
                          </span>
                        </div>)}
                    </div>

                    {/* Main CTA Button */}
                    <button onClick={handlePurchaseClick} className="btn-shine w-full bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-lg font-extrabold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6" data-id="element-352">
                      SÍ, QUIERO LA OFERTA AHORA
                      <ArrowRight className="w-5 h-5" data-id="element-353" />
                    </button>

                    {/* Payment methods */}
                    <div className="text-center mb-6" data-id="element-354">
                      <p className="text-xs uppercase tracking-wider mb-3 font-[700] text-[#B0B0B0]" data-id="element-355">
                        PAGO ÚNICO CON ACCESO DE POR VIDA ☑️
                      </p>
                      <div className="flex flex-col items-center gap-3" data-id="element-356">
                        <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain rounded-lg" loading="lazy" decoding="async" width={320} height={64} data-id="element-357" />
                        <div className="w-full max-w-[320px]" data-id="element-358">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-359">
                            Tu compra está protegida
                          </p>
                          <img src="https://cdn.magicpatterns.com/uploads/uPDFKg71qahzFUGpnG84Z3/capture_260410_163646.png" alt="Pago seguro garantizado" className="w-full object-contain rounded-xl" loading="lazy" decoding="async" width={320} height={100} data-id="element-360" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trust badges on dark background */}
                  <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-6 border-t-2 border-gray-700" data-id="element-361">
                    <div className="grid grid-cols-2 gap-4 items-center" data-id="element-362">
                      <div className="flex flex-col items-center gap-2" data-id="element-363">
                        <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain" loading="lazy" decoding="async" width={120} height={120} data-id="element-364" />
                        <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-365">
                          GARANTÍA DE SATISFACCIÓN
                        </span>
                      </div>
                      <div className="flex flex-col items-center gap-2" data-id="element-366">
                        <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain" loading="lazy" decoding="async" width={156} height={40} data-id="element-367" />
                        <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-368">
                          COMPRA 100% SEGURA
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div> : <motion.div key="combo" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-369">
                {/* CARD 2: COMBO / UPSELL */}
                <div className="bg-[#0a1a3a] rounded-2xl border-[3px] border-yellow-400 relative overflow-hidden animate-card-glow shadow-2xl" data-id="element-370">
                  <div className="bg-yellow-400 text-gray-900 font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" data-id="element-371">
                    ESPECIALIZACIÓN INDUSTRIAL COMPLETA
                  </div>
                  <p className="text-center font-bold text-white bg-gray-900 py-2 px-4 text-xs md:text-sm uppercase tracking-wide" data-id="element-372">
                    MAESTRO ELÉCTRICO + PLC MASTER
                  </p>

                  {/* Product Image — with subtle vibration */}
                  <div className="relative bg-gradient-to-b from-gray-100 to-gray-200 px-4 border-b border-gray-300 pt-[0px] pb-[0px] pl-[0px] pr-[0px]" data-id="element-373">
                    <img src="https://cdn.magicpatterns.com/uploads/tvNcA7mMJGGXnANf1wZviT/aasdadasdasd.png" alt="Combo Maestro Eléctrico + Especialización Industrial" className="w-full h-auto object-cover rounded-lg shadow-inner animate-subtle-vibrate" loading="lazy" decoding="async" width={600} height={400} data-id="element-374" />
                  </div>

                  {/* Circular Offer Badge */}
                  <div className="flex justify-center -mt-14 mb-4 relative z-10" data-id="element-375">
                    <div className="relative" data-id="element-376">
                      {/* Rotating outer ring */}
                      <div className="absolute inset-0 rounded-full animate-rotate-border" style={{
                    background: 'conic-gradient(from 0deg, #fbbf24, #f59e0b, #d97706, #fbbf24, #f59e0b, #d97706, #fbbf24)',
                    padding: '3px',
                    borderRadius: '9999px'
                  }} data-id="element-377"></div>
                      {/* Inner circle */}
                      <div className="relative w-44 h-44 md:w-52 md:h-52 rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-4 border-yellow-400 flex flex-col items-center justify-center text-center p-4 animate-offer-circle-pulse" style={{
                    margin: '3px'
                  }} data-id="element-378">
                        <span className="text-yellow-400 font-extrabold text-xs md:text-sm uppercase leading-tight tracking-wide" data-id="element-379">
                          ¡COMBO TOTAL
                        </span>
                        <span className="text-yellow-400 font-extrabold text-xs md:text-sm uppercase leading-tight tracking-wide" data-id="element-380">
                          PROFESIONAL!
                        </span>
                        <div className="w-12 h-px bg-yellow-500/50 my-1.5" data-id="element-381"></div>
                        <span className="text-white text-[10px] md:text-[11px] font-bold uppercase leading-tight" data-id="element-382">
                          INCLUYE:
                        </span>
                        <span className="text-gray-300 text-[9px] md:text-[10px] font-medium leading-tight mt-0.5" data-id="element-383">
                          2 MANUALES + 9 BONOS
                        </span>
                        <span className="text-gray-300 text-[9px] md:text-[10px] font-medium leading-tight" data-id="element-384">
                          DE REGALO
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 pt-2" data-id="element-385">
                    {/* Pricing block — Premium gold gradient title + green price */}
                    <div className="text-center mb-6" data-id="element-386">
                      <p className="text-xl md:text-2xl font-extrabold mb-3 uppercase tracking-wide text-gradient-gold leading-tight" data-id="element-387">
                        MAESTRO ELÉCTRICO + PLC MASTER + 9 BONOS DE REGALO
                      </p>
                      <p className="text-5xl md:text-6xl font-extrabold leading-none mb-2" style={{
                    color: '#00FF00',
                    textShadow: '0 0 10px rgba(0,255,0,0.5), 0 0 30px rgba(0,255,0,0.2)'
                  }} data-id="element-388">
                        $29.990{' '}
                        <span className="text-2xl font-normal text-gray-400" data-id="element-389">
                          ARS
                        </span>
                      </p>
                      <p className="text-lg text-gray-400 font-medium" data-id="element-390">
                        Antes{' '}
                        <span className="line-through text-gray-500" data-id="element-391">
                          $99.980 ARS
                        </span>
                      </p>
                      <p className="text-sm font-bold text-red-500 uppercase tracking-wide mt-1" data-id="element-392">
                        AHORRÁ MÁS DEL 60%
                      </p>
                    </div>

                    {/* Installments badge */}
                    <div className="flex justify-center mb-6" data-id="element-393">
                      <div className="badge-shimmer bg-green-900/40 text-green-400 font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border border-green-500/30" data-id="element-394">
                        <CreditCard className="w-4 h-4" data-id="element-395" />
                        Hasta 3 cuotas sin interés
                      </div>
                    </div>

                    {/* Urgency Header */}
                    <div className="bg-black rounded-lg px-4 py-2 mb-3 text-center border border-yellow-500/60" data-id="element-396">
                      <p className="text-xs md:text-sm font-bold text-white uppercase tracking-wide" data-id="element-397">
                        OFERTA VÁLIDA SOLO POR HOY:{' '}
                        <span className="text-yellow-400 font-extrabold" data-id="element-398">
                          {weekdayDate}
                        </span>
                      </p>
                    </div>

                    {/* Urgency Counters Row */}
                    <div className="bg-black rounded-xl px-4 py-3 mb-3 border border-gray-800 flex items-center justify-center gap-4" data-id="element-399">
                      <div className="flex items-center gap-2" data-id="element-400">
                        <Eye className="w-4 h-4 text-gray-400" data-id="element-401" />
                        <span className="text-lg font-bold" style={{
                      color: '#00FF00',
                      textShadow: '0 0 8px rgba(0,255,0,0.6), 0 0 20px rgba(0,255,0,0.3)'
                    }} data-id="element-402">
                          {viewers}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-403">
                          Personas mirando
                          <br data-id="element-404" />
                          en este momento
                        </span>
                      </div>
                      <div className="w-px h-8 bg-gray-700" data-id="element-405"></div>
                      <div className="flex items-center gap-2" data-id="element-406">
                        <Ticket className="w-4 h-4 text-gray-400" data-id="element-407" />
                        <span className="text-lg font-bold" style={{
                      color: '#FF2020',
                      textShadow: '0 0 8px rgba(255,32,32,0.6), 0 0 20px rgba(255,32,32,0.3)'
                    }} data-id="element-408">
                          {upsellSpots}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-409">
                          Cupos promocionales
                          <br data-id="element-410" />
                          disponibles
                        </span>
                      </div>
                    </div>

                    {/* Professional Counter */}
                    <div className="bg-black rounded-xl px-6 py-4 mb-6 border border-gray-800" data-id="element-411">
                      <div className="flex flex-col items-center" data-id="element-412">
                        <span className="text-4xl font-extrabold tracking-tight" style={{
                      color: '#00FF00',
                      textShadow: '0 0 10px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.3), 0 0 50px rgba(0,255,0,0.15)'
                    }} data-id="element-413">
                          {comboBuyers}
                        </span>
                        <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em] mt-1" data-id="element-414">
                          PROFESIONALES YA ACCEDIERON
                        </span>
                      </div>
                    </div>

                    {/* Inventory List - 10 Points */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-8 bg-black/30 p-5 rounded-xl border border-white/5" data-id="element-415">
                      {['Manual Maestro Eléctrico', 'Manual PLC Master (Industrial)', 'Bono 1: Curso Paneles Solares', 'Bono 2: Plantillas Presupuesto', 'Bono 3: Eficiencia en Motores', 'Bono 4: Protocolo Verificación', 'Bono 5: Reglamentaciones AEA', 'Bono 6: Lógica de Programación', 'Bono 7: Planos Industriales', 'Bono 8: Diagnóstico de Fallas', 'Bono 9: Certificado Final'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-416">
                          <Check className="w-4 h-4 text-[#FFFF00] flex-shrink-0 mt-0.5" data-id="element-417" />
                          <span className="text-gray-200 font-medium text-xs md:text-sm" data-id="element-418">
                            {item}
                          </span>
                        </div>)}
                    </div>

                    {/* Main CTA Button */}
                    <button onClick={handleComboPurchaseClick} className="btn-shine w-full bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-lg md:text-xl font-extrabold py-5 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6" data-id="element-419">
                      SÍ, QUIERO EL COMBO COMPLETO →
                    </button>

                    {/* Payment methods */}
                    <div className="text-center mb-6" data-id="element-420">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-421">
                        PAGO ÚNICO CON ACCESO DE POR VIDA ☑️
                      </p>
                      <div className="flex flex-col items-center gap-3" data-id="element-422">
                        <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain" loading="lazy" decoding="async" width={320} height={64} data-id="element-423" />
                        <div className="w-full max-w-[320px]" data-id="element-424">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-425">
                            Tu compra está protegida
                          </p>
                          <img src="https://cdn.magicpatterns.com/uploads/uPDFKg71qahzFUGpnG84Z3/capture_260410_163646.png" alt="Pago seguro garantizado" className="w-full object-contain rounded-xl" loading="lazy" decoding="async" width={320} height={100} data-id="element-426" />
                        </div>
                      </div>
                    </div>

                    {/* Trust footer */}
                    <div className="flex items-center justify-center gap-6 pt-4 border-t border-blue-800/50" data-id="element-427">
                      <div className="flex items-center gap-1.5" data-id="element-428">
                        <Shield className="w-4 h-4 text-green-400" data-id="element-429" />
                        <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-430">
                          Pago 100% encriptado
                        </span>
                      </div>
                      <div className="w-px h-5 bg-blue-800/50" data-id="element-431"></div>
                      <div className="flex items-center gap-1.5" data-id="element-432">
                        <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía" className="w-5 h-5 object-contain" loading="lazy" decoding="async" width={20} height={20} data-id="element-433" />
                        <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-434">
                          Garantía sin riesgo
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Trust badges on dark background */}
                  <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-6 border-t-2 border-gray-700" data-id="element-435">
                    <div className="grid grid-cols-2 gap-4 items-center" data-id="element-436">
                      <div className="flex flex-col items-center gap-2" data-id="element-437">
                        <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain" loading="lazy" decoding="async" width={120} height={120} data-id="element-438" />
                        <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-439">
                          GARANTÍA DE SATISFACCIÓN
                        </span>
                      </div>
                      <div className="flex flex-col items-center gap-2" data-id="element-440">
                        <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain" loading="lazy" decoding="async" width={156} height={40} data-id="element-441" />
                        <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-442">
                          COMPRA 100% SEGURA
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* MercadoLíder Platinum Trust Seal */}
          <div className="flex justify-center" data-id="element-443">
            <img src="https://cdn.magicpatterns.com/uploads/wihdMwuqBpQKvri8XHgUZZ/Gemini_Generated_Image_jbp7w2jbp7w2jbp7.png" alt="MercadoLíder Platinum - 8413 ventas" className="w-full max-w-2xl object-contain" loading="lazy" decoding="async" width={672} height={100} data-id="element-444" />
          </div>

          {/* Lifetime Access Bonus Section */}
          <div className="mt-12 mb-8 flex justify-center px-4" data-id="element-445">
            <div className="w-full max-w-2xl bg-[#000D1A] rounded-[12px] border border-gray-800 shadow-2xl overflow-hidden relative group" data-id="element-446">
              {/* Subtle Glow Effect */}
              <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-447"></div>

              <div className="p-8 md:p-12 flex flex-col items-center text-center relative z-10 pl-[15px] pr-[15px]" data-id="element-448">
                {/* Authority Seal */}
                <div className="relative mb-8" data-id="element-449">
                  <div className="absolute inset-0 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" data-id="element-450"></div>
                  <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full shadow-[0_0_50px_rgba(234,179,8,0.3)] border-4 border-yellow-500/20 overflow-hidden" data-id="element-451">
                    <img src="https://cdn.magicpatterns.com/uploads/oLERSYzpMkuFusC7XS9pGH/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Sello de Acceso Vitalicio" className="w-full h-full object-cover" data-id="element-452" />
                  </div>
                </div>

                {/* Text Hierarchy */}
                <span className="text-[#00FF00] text-[10px] md:text-xs font-black tracking-[0.2em] uppercase mb-3" data-id="element-453">
                  BONO EXTRA EXCLUSIVO
                </span>

                <h3 className="text-white text-3xl md:text-4xl font-bold mb-4 tracking-tight" data-id="element-454">
                  Acceso Vitalicio
                </h3>

                <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-lg mb-8" data-id="element-455">
                  Olvidate de las mensualidades. Con un solo pago tenés acceso
                  de por vida a todo el material y a las futuras actualizaciones
                  sin pagar un centavo más.
                </p>

                {/* Status Component */}
                <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#062010] border border-[#00FF00]/40 shadow-[0_0_15px_rgba(0,255,0,0.1)]" data-id="element-456">
                  <Check className="w-4 h-4 text-[#00FF00] stroke-[3px]" data-id="element-457" />
                  <span className="text-[#00FF00] text-xs md:text-sm font-black uppercase tracking-widest" data-id="element-458">
                    INCLUIDO GRATIS
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-8 px-4 bg-white" data-id="element-459">
        <div className="container mx-auto max-w-6xl" data-id="element-460">
          <div className="text-center mb-12" data-id="element-461">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-462">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-463">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="space-y-16" data-id="element-464">
            <div className="flex justify-center" data-id="element-465">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl" data-id="element-466">
                <video src="https://cdn.magicpatterns.com/uploads/2sNKU8uzEEtWZTuVPY2CeA/0126.undefined" className="w-full" autoPlay loop muted playsInline data-id="element-467" />
              </div>
            </div>
            <ImageCarousel images={contentImages} data-id="element-468" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - WhatsApp style, right before FAQ */}
      <section className="py-8 px-4 bg-gradient-to-b from-gray-100 to-gray-50" data-id="element-469">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-470">
          <div className="text-center mb-8" data-id="element-471">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-472">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-473" />)}
            </div>
            <h2 className="text-lg font-bold text-gray-800 uppercase" data-id="element-474">
              MÁS DE 6.250 ELECTRICISTAS SATISFECHOS
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-475">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-4 mb-8" data-id="element-476">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <div key={i} className="flex items-start gap-3" data-id="element-477">
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-478">
                  {t.name.charAt(0)}
                </div>
                <div className="flex-grow" data-id="element-479">
                  <div className="flex items-center gap-2 mb-1" data-id="element-480">
                    <span className="font-bold text-sm text-gray-800" data-id="element-481">
                      {t.name}
                    </span>
                    {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-482">
                        ✓ Verificado
                      </span>}
                  </div>
                  <div className="chat-bubble" data-id="element-483">
                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-484">
                      {t.msg}
                    </p>
                    <p className="text-[10px] text-gray-400 text-right mt-1" data-id="element-485">
                      {t.time}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-486">
              <button onClick={showMoreTestimonials} className="btn-shine bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 font-medium py-2.5 px-6 rounded-lg transition-colors text-sm" data-id="element-487">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-488" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 bg-white" data-id="element-489">
        <div className="container mx-auto max-w-3xl" data-id="element-490">
          <div className="text-center mb-12" data-id="element-491">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-492">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-493">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-494">
            <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-495" />
            <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-496" />
            <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-497" />
            <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-498" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-499" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-500" />
            <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-501" />
            <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-502" />
            <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-503" />
            <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-504" />
            <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-505" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4 mb-24 md:mb-32" data-id="element-506">
        <div className="container mx-auto max-w-6xl" data-id="element-507">
          <p className="text-center text-sm" data-id="element-508">
            © 2025 Maestro Eléctrico. Todos los derechos reservados.
          </p>
          <p className="text-center text-sm mt-2" data-id="element-509">
            Diagramas y circuitos de mando para profesionales.
          </p>
        </div>
      </footer>

      {/* STICKY BOTTOM BAR */}
      <AnimatePresence data-id="element-510">
        {showStickyBar && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6" data-id="element-511">
            <div className="container mx-auto max-w-2xl relative" data-id="element-512">
              {/* Floating Price Animation */}
              <AnimatePresence data-id="element-513">
                {floatingPrice.visible && <motion.div initial={{
              opacity: 0,
              y: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              y: -40,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -60,
              scale: 1.1
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className={`absolute left-1/2 -translate-x-1/2 md:left-24 md:translate-x-0 top-0 z-50 font-black text-lg pointer-events-none drop-shadow-sm ${floatingPrice.color}`} data-id="element-514">
                    {floatingPrice.value}
                  </motion.div>}
              </AnimatePresence>

              <div className="bg-white/90 backdrop-blur-md border border-gray-200 rounded-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] p-3 md:p-4 flex md:flex-row md:gap-4 pt-[5px] pb-[5px] pl-[12px] pr-[12px] gap-[0px] flex-col items-center justify-start" data-id="element-515">
                {/* Offer Toggle */}
                <div className="flex bg-gray-100 p-1 rounded-xl w-full md:w-auto" data-id="element-516">
                  <button onClick={() => {
                setSelectedOffer('solo');
                triggerFloatingPrice('$19.990 ARS', 'text-[#0a1a3a]');
              }} className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all ${selectedOffer === 'solo' ? 'bg-[#0a1a3a] text-white shadow-md' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-517">
                    SOLO MANUAL
                  </button>
                  <button onClick={() => {
                setSelectedOffer('combo');
                triggerFloatingPrice('$29.990 ARS', 'text-green-600');
              }} className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${selectedOffer === 'combo' ? 'bg-yellow-500 text-blue-900 shadow-md' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-518">
                    COMBO PLC
                    <Star className={`w-3 h-3 ${selectedOffer === 'combo' ? 'fill-blue-900' : 'fill-gray-400'}`} data-id="element-519" />
                  </button>
                </div>

                {/* Price Display */}
                <div className="hidden md:flex flex-col items-start px-2" data-id="element-520">
                  <span className="text-[10px] text-gray-400 uppercase font-bold leading-none" data-id="element-521">
                    Total hoy
                  </span>
                  <span className="text-xl font-black text-gray-900 leading-none" data-id="element-522">
                    {selectedOffer === 'solo' ? '$19.990' : '$29.990'}
                  </span>
                </div>

                {/* CTA Button */}
                <button onClick={selectedOffer === 'solo' ? handlePurchaseClick : handleComboPurchaseClick} className="w-full md:w-auto flex-1 bg-green-600 hover:bg-green-500 text-white font-black py-3 px-6 rounded-xl shadow-lg shadow-green-600/20 transition-all flex items-center justify-center gap-2 group" data-id="element-523">
                  <span className="uppercase tracking-tight" data-id="element-524">
                    COMPRAR AHORA
                  </span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-525" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}